import React, { useState, useEffect, useRef } from "react";
import Slider from "react-slick";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import { Button, Headline } from "./ui";

const ImagePlaceholder = ({ className }) => (
  <div className={`${className} bg-gray-200 relative overflow-hidden`}>
    <div className="absolute inset-0 bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200 animate-shimmer bg-[length:200%_100%]" />
  </div>
);
// import { Headline } from "";

export function GenericCard({
  images,
  headline,
  info = [],
  price,
  buttonText,
  onButtonClick,
}) {
  const [slidesToShow, setSlidesToShow] = useState(1);
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const sliderRef = useRef(null);

  useEffect(() => {
    const handleResize = () => {
      setSlidesToShow(window.innerWidth > 1000 ? 3 : 1);
    };

    window.addEventListener("resize", handleResize);
    handleResize();

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Load images
  useEffect(() => {
    if (images && images.length > 0) {
      const imagePromises = images.map((src) => {
        return new Promise((resolve) => {
          const img = new Image();
          img.onload = () => resolve();
          img.onerror = () => resolve(); // Still resolve on error
          img.src = `${process.env.PUBLIC_URL}${src}`;
        });
      });

      Promise.all(imagePromises).then(() => {
        setImagesLoaded(true);
      });
    }
  }, [images]);

  const settings = {
    dots: true, // Enable dots
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: false,
    appendDots: (dots) => (
      <div className="flex justify-center mt-4">
        <ul className="flex space-x-2"> {dots} </ul>
      </div>
    ),
    customPaging: () => (
      <div className="w-3 h-3 rounded-full bg-gray-300 hover:bg-gray-500 transition-colors duration-200"></div>
    ),
  };

  return (
    <div
      className="rounded-sm overflow-hidden flex flex-col shadow-lg cursor-pointer hover:shadow-xl transition-shadow duration-300"
      style={{ backgroundColor: "var(--color-secondary)" }}
      onClick={onButtonClick}
    >
      {/* Slider */}
      <div className="relative w-full">
        <Slider ref={sliderRef} {...settings}>
          {!imagesLoaded
            ? // Show placeholders for each image while they load
              images.map((_, index) => (
                <div key={`placeholder-${index}`}>
                  <ImagePlaceholder className="w-full aspect-[16/11]" />
                </div>
              ))
            : // Show actual images when loaded
              images.map((src, index) => (
                <div key={index}>
                  <img
                    src={`${process.env.PUBLIC_URL}${src}`}
                    alt={`Slide ${index}`}
                    className="w-full aspect-[16/11] object-cover"
                  />
                </div>
              ))}
        </Slider>
        <button
          className="absolute top-1/2 left-2 transform -translate-y-1/2 bg-transparent text-white border-none cursor-pointer text-2xl rounded-full w-11 h-11 z-10 flex items-center justify-center hover:bg-black hover:bg-opacity-20 transition-colors duration-200"
          onClick={(e) => {
            e.stopPropagation();
            sliderRef.current.slickPrev();
          }}
        >
          <FontAwesomeIcon
            icon={faChevronLeft}
            style={{ color: "var(--color-primary)" }}
          />
        </button>
        <button
          className="absolute top-1/2 right-2 transform -translate-y-1/2 bg-transparent text-white border-none cursor-pointer text-2xl rounded-full w-11 h-11 z-10 flex items-center justify-center hover:bg-black hover:bg-opacity-20 transition-colors duration-200"
          onClick={(e) => {
            e.stopPropagation();
            sliderRef.current.slickNext();
          }}
        >
          <FontAwesomeIcon
            icon={faChevronRight}
            style={{ color: "var(--color-primary)" }}
          />
        </button>
      </div>

      {/* Headline */}
      <Headline variant="generic-card" className="text-left pt-4 pb-0 px-4">
        {headline}
      </Headline>

      {/* Information */}
      <div className="flex flex-col px-4 gap-2 pb-4">
        {info.map(({ label, value }, index) => (
          <div className="flex justify-start items-baseline gap-4" key={index}>
            <span
              className="text-sm sm:text-md w-5 "
              style={{ color: "var(--color-primary)" }}
            >
              {label}
            </span>
            <span
              className="text-sm sm:text-md font-light"
              style={{ color: "var(--color-primary)" }}
            >
              {value}
            </span>
          </div>
        ))}
      </div>

      {/* Bottom section */}
      <div className="flex justify-between items-center mt-auto">
        <span
          className="text-lg sm:text-xl  px-4 tracking-wide font-semibold  mt-[-20px]"
          style={{ color: "var(--color-primary)" }}
        >
          {price}
        </span>
        <Button
          onClick={(e) => {
            e.stopPropagation();
            onButtonClick();
          }}
          variant="secondary"
          size="medium"
          // className="px-4 py-4 w-40 text-lg font-semibold"
          // style={{ borderRadius: "0 0 8px 0" }}
        >
          {buttonText}
        </Button>
      </div>
    </div>
  );
}
